import * as React from 'react'
import { Data } from 'rete/types/core/data'

import { Tab, TeamExtentData } from "./project_editor"
import { DatasetCache } from '../map/map_view'
import { Layer } from '../state'
import { extentChoiceLabel, NON_INPUT_COMPONENT_CATEGORIES } from "./public_share_settings"

interface ToolbarProps {
  backButtonPath: string
  teamId: number
  projectName: string
  hasUnsavedChanges: boolean
  currentTab: Tab
  isProcessing: boolean
  isLoading: boolean
  loadingQueue: number[]
  datasetOutputCache: DatasetCache
  projectLayers: Record<number, Layer>
  setProjectName: (name: string) => void
  saveProject: () => void
  setCurrentTab: (tab: Tab) => void
  autoProcessing: boolean
  setAutoProcessing: (autoprocessing: boolean) => void
  manualProcessing: () => void
  setShowAP: () => void
  showAP: boolean
  setShowExtent: (boolean) => void
  zoomLevel: number
  mapViewZoom: number
  mapViewCenter: [number, number]
  isUserGuest: boolean
  extraInfo: string | null
  projectId: number
  teamExtents: TeamExtentData[]
  currentExtentId: number | null
  projectModel: Data | null
  availableComponents: { name: string, label: string, category: string }[]
  availableMapLayers: { id: string, label: string, category: string, active: boolean }[]
}

interface PublicProjectShare {
  id: number
  publicly_shared: boolean
  share_code: string
  share_url: string | null
}

const SHARE_CODE_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9_-]*[A-Za-z0-9])?$/

function publicProjectFromRecord(record: { id: number, publicly_shared?: boolean, share_code?: string | null, share_url?: string | null }): PublicProjectShare {
  const shareUrl = typeof record.share_url === "string" ? record.share_url : null
  const fromUrl = shareUrl ? shareUrl.slice(shareUrl.lastIndexOf("/") + 1) : ""
  return {
    id: record.id,
    publicly_shared: !!record.publicly_shared,
    share_code: typeof record.share_code === "string" && record.share_code ? record.share_code : fromUrl,
    share_url: shareUrl,
  }
}

function shareLinkPrefix(url: string, code: string): string {
  if (code && url.endsWith("/" + code)) return url.slice(0, -code.length)
  const slash = url.lastIndexOf("/")
  return slash >= 0 ? url.slice(0, slash + 1) : url
}

interface PersonalAgentKey {
  provider: string
  label: string
}

interface PublicAgentDraft {
  keySource: "" | "personal" | "custom"
  provider: string
  model: string
  apiKey: string
  keyPresent: boolean
}

const EMPTY_AGENT_DRAFT: PublicAgentDraft = {
  keySource: "",
  provider: "",
  model: "",
  apiKey: "",
  keyPresent: false,
}

const PUBLIC_AGENT_PROVIDERS: { id: string, label: string, models: { id: string, label: string }[] }[] = [
  {
    id: "groq",
    label: "Groq",
    models: [
      { id: "qwen/qwen3-32b", label: "Qwen3 32B" },
      { id: "llama-3.3-70b-versatile", label: "Llama 3.3 70B" },
      { id: "llama-3.1-8b-instant", label: "Llama 3.1 8B (fast)" },
    ],
  },
  {
    id: "anthropic",
    label: "Claude (Anthropic)",
    models: [
      { id: "claude-opus-4-8", label: "Claude Opus 4.8" },
      { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
      { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
      { id: "claude-opus-4-7", label: "Claude Opus 4.7" },
      { id: "claude-opus-4-6", label: "Claude Opus 4.6" },
      { id: "claude-opus-4-5-20251101", label: "Claude Opus 4.5" },
    ],
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    models: [
      { id: "deepseek-flash", label: "DeepSeek Flash" },
      { id: "deepseek-v4-pro", label: "DeepSeek Pro" },
    ],
  },
]

function publicAgentProviderLabel(id: string) {
  const entry = PUBLIC_AGENT_PROVIDERS.find(provider => provider.id === id)
  return entry ? entry.label : id
}

function publicAgentModels(provider: string, current: string) {
  const entry = PUBLIC_AGENT_PROVIDERS.find(item => item.id === provider)
  const models = entry ? entry.models.map(model => ({ id: model.id, label: model.label })) : []
  if (current && !models.some(model => model.id === current)) models.push({ id: current, label: current })
  return models
}

function agentDraftFromRecord(record: any): PublicAgentDraft {
  const source = record?.agent_key_source === "personal" || record?.agent_key_source === "custom" ? record.agent_key_source : ""
  return {
    keySource: source,
    provider: typeof record?.agent_provider === "string" ? record.agent_provider : "",
    model: typeof record?.agent_model === "string" ? record.agent_model : "",
    apiKey: "",
    keyPresent: !!record?.agent_key_present,
  }
}

function personalKeysFromResponse(data: any): PersonalAgentKey[] {
  if (!Array.isArray(data?.personal_keys)) return []
  const keys: PersonalAgentKey[] = []
  data.personal_keys.forEach((entry: any) => {
    if (!entry || typeof entry.provider !== "string" || typeof entry.label !== "string") return
    keys.push({ provider: entry.provider, label: entry.label })
  })
  return keys
}

interface PublicShareItem {
  id: string
  label: string
  detail?: string
  category?: string
  active?: boolean
}

function disambiguateLabels(items: PublicShareItem[]): PublicShareItem[] {
  const counts = new Map<string, number>()
  for (const item of items) {
    counts.set(item.label, (counts.get(item.label) ?? 0) + 1)
  }
  const seen = new Map<string, number>()
  return items.map(item => {
    if ((counts.get(item.label) ?? 0) < 2) return item
    const index = (seen.get(item.label) ?? 0) + 1
    seen.set(item.label, index)
    return { ...item, label: `${item.label} (${index})` }
  })
}

function shareItemDomId(id: string): string {
  return `public-share-${encodeURIComponent(id)}`
}

function extentIdsFromSelection(selection: Set<string>): number[] {
  const ids: number[] = []
  selection.forEach(id => {
    if (!id.startsWith("extent-")) return
    const value = Number(id.slice("extent-".length))
    if (Number.isFinite(value)) ids.push(value)
  })
  return ids
}

function groupShareItems(items: PublicShareItem[], sortItems = true): { category: string, items: PublicShareItem[] }[] | null {
  if (!items.some(item => item.category)) return null
  const groups: { category: string, items: PublicShareItem[] }[] = []
  const indexByCategory = new Map<string, number>()
  for (const item of items) {
    const category = item.category || "Other"
    let index = indexByCategory.get(category)
    if (index === undefined) {
      index = groups.length
      indexByCategory.set(category, index)
      groups.push({ category, items: [] })
    }
    groups[index].items.push(item)
  }
  if (sortItems) {
    for (const group of groups) {
      group.items.sort((a, b) => a.label.localeCompare(b.label))
    }
  }
  return groups
}

function PublicShareChecklist({
  title,
  emptyLabel,
  items,
  selectedIds,
  onToggle,
  onSetAll,
  disabled = false,
  sortItems = true,
  showSelectAll = true,
  filterable = false,
  hint,
}: {
  title: string
  emptyLabel: string
  items: PublicShareItem[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onSetAll: (ids: string[], selected: boolean) => void
  disabled?: boolean
  sortItems?: boolean
  showSelectAll?: boolean
  filterable?: boolean
  hint?: string
}) {
  const [query, setQuery] = React.useState("")
  const visibleItems = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return items
    return items.filter(item => item.label.replace(/\u00a0/g, " ").toLowerCase().includes(needle))
  }, [items, query])
  const selectedCount = items.filter(item => selectedIds.has(item.id)).length
  const allSelected = visibleItems.length > 0 && visibleItems.every(item => selectedIds.has(item.id))
  const someSelected = visibleItems.some(item => selectedIds.has(item.id)) && !allSelected
  const selectAllId = `public-share-all-${title.replace(/\s+/g, "-").toLowerCase()}`
  const groups = groupShareItems(visibleItems, sortItems)

  return (
    <div className="d-flex flex-column" style={{ minWidth: 0, flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h6 className="mb-0">{title}</h6>
        <span className="text-muted small">{selectedCount}/{items.length}</span>
      </div>
      {hint && <p className="text-muted small mb-2">{hint}</p>}
      {items.length === 0 ? (
        <p className="text-muted small mb-0">{emptyLabel}</p>
      ) : (
        <>
          {filterable && (
            <input
              type="search"
              className="form-control form-control-sm mb-2"
              placeholder={`Search ${title.toLowerCase()}`}
              value={query}
              disabled={disabled}
              aria-label={`Search ${title.toLowerCase()}`}
              onChange={event => setQuery(event.target.value)}
            />
          )}
          {showSelectAll && (
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id={selectAllId}
              checked={allSelected}
              disabled={disabled || visibleItems.length === 0}
              ref={element => {
                if (element) element.indeterminate = someSelected
              }}
              onChange={() => onSetAll(visibleItems.map(item => item.id), !allSelected)}
            />
            <label className="form-check-label" htmlFor={selectAllId}>Select all</label>
          </div>
          )}
          <div className="border rounded p-2" style={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }}>
            {visibleItems.length === 0 && <p className="text-muted small mb-0">No matches.</p>}
            {(groups ?? [{ category: "", items: visibleItems }]).map((group, groupIndex) => (
              <div key={group.category || "all"}>
                {group.category && (
                  <div className={`small font-weight-bold text-muted mb-1${groupIndex > 0 ? " mt-2" : ""}`}>{group.category}</div>
                )}
                {group.items.map(item => (
                  <div className="form-check" key={item.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={shareItemDomId(item.id)}
                      checked={selectedIds.has(item.id)}
                      disabled={disabled}
                      onChange={() => onToggle(item.id)}
                    />
                    <label className="form-check-label" htmlFor={shareItemDomId(item.id)}>
                      {item.label}{item.active ? "*" : ""}
                      {item.detail && <span className="text-muted"> — {item.detail}</span>}
                    </label>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

interface IntroLayerRef {
  id: number
  nodeId?: number
  label: string
}

function mentionAtCursor(value: string, cursor: number, labels: string[]): { start: number, query: string } | null {
  const before = value.slice(0, cursor)
  const match = /(?:^|[\s])@([^\n@]*)$/.exec(before)
  if (!match) return null
  const query = match[1]
  const completed = labels.some(label => query === `${label}:` || query.startsWith(`${label}: `))
  if (completed || /^\[[^\]]*\]\((?:#\d+|layer:\d+)\)$/.test(query)) return null
  return { start: cursor - query.length - 1, query }
}

function layerMentionToken(layer: IntroLayerRef): string {
  const label = layer.label.replace(/[\[\]]/g, "")
  const target = layer.nodeId != null ? `#${layer.nodeId}` : `layer:${layer.id}`
  return `@[${label}](${target})`
}

function chooseAgentProvider(draft: PublicAgentDraft, provider: string): PublicAgentDraft {
  const models = publicAgentModels(provider, "")
  const model = models.some(entry => entry.id === draft.model) ? draft.model : (models[0]?.id ?? "")
  return { ...draft, provider, model, apiKey: "" }
}

function PublicAgentKeyFields({
  agent,
  personalKeys,
  disabled,
  onChange,
}: {
  agent: PublicAgentDraft
  personalKeys: PersonalAgentKey[]
  disabled: boolean
  onChange: (next: PublicAgentDraft) => void
}) {
  const personalOptions = personalKeys.slice()
  if (agent.keySource === "personal" && agent.provider && !personalOptions.some(key => key.provider === agent.provider)) {
    personalOptions.push({ provider: agent.provider, label: publicAgentProviderLabel(agent.provider) })
  }
  const models = publicAgentModels(agent.provider, agent.model)

  const selectSource = (keySource: PublicAgentDraft["keySource"]) => {
    if (keySource === "") {
      onChange({ ...agent, keySource, apiKey: "" })
      return
    }
    if (keySource === "personal") {
      const provider = personalOptions.some(key => key.provider === agent.provider)
        ? agent.provider
        : (personalOptions[0]?.provider ?? "")
      onChange(chooseAgentProvider({ ...agent, keySource }, provider))
      return
    }
    const provider = PUBLIC_AGENT_PROVIDERS.some(entry => entry.id === agent.provider)
      ? agent.provider
      : PUBLIC_AGENT_PROVIDERS[0].id
    onChange(chooseAgentProvider({ ...agent, keySource }, provider))
  }

  return (
    <div className="mt-3">
      <h6 className="mb-2">AEGIS model</h6>
      <select
        className="form-control form-control-sm mb-2"
        value={agent.keySource}
        disabled={disabled}
        aria-label="Public AEGIS key"
        onChange={event => selectSource(event.target.value as PublicAgentDraft["keySource"])}
      >
        <option value="">No public key</option>
        <option value="personal">Use a personal key</option>
        <option value="custom">Enter an API key</option>
      </select>
      {agent.keySource === "personal" && (
        personalOptions.length === 0 ? (
          <div className="text-muted small">Save a personal API key on your account to use it here.</div>
        ) : (
          <>
            <select
              className="form-control form-control-sm mb-2"
              value={agent.provider}
              disabled={disabled}
              aria-label="Personal API key"
              onChange={event => onChange(chooseAgentProvider(agent, event.target.value))}
            >
              {personalOptions.map(key => (
                <option key={key.provider} value={key.provider}>{key.label}</option>
              ))}
            </select>
            <div className="text-muted small mb-2">Visitors use this key from your account. It is not copied into the share.</div>
          </>
        )
      )}
      {agent.keySource === "custom" && (
        <>
          <select
            className="form-control form-control-sm mb-2"
            value={agent.provider}
            disabled={disabled}
            aria-label="API provider"
            onChange={event => onChange(chooseAgentProvider(agent, event.target.value))}
          >
            {PUBLIC_AGENT_PROVIDERS.map(provider => (
              <option key={provider.id} value={provider.id}>{provider.label}</option>
            ))}
          </select>
          <input
            className="form-control form-control-sm mb-2"
            type="password"
            value={agent.apiKey}
            disabled={disabled}
            autoComplete="off"
            placeholder={agent.keyPresent ? "Saved key in use. Enter a new key to replace it." : "API key"}
            aria-label="API key"
            onChange={event => onChange({ ...agent, apiKey: event.target.value })}
          />
          <div className="text-muted small mb-2">Stored encrypted for this share. Leave blank to keep the saved key.</div>
        </>
      )}
      {agent.keySource !== "" && models.length > 0 && (
        <select
          className="form-control form-control-sm"
          value={agent.model}
          disabled={disabled}
          aria-label="Model"
          onChange={event => onChange({ ...agent, model: event.target.value })}
        >
          {models.map(model => (
            <option key={model.id} value={model.id}>{model.label}</option>
          ))}
        </select>
      )}
    </div>
  )
}

function AegisInstructionsEditor({
  value,
  onChange,
  intro,
  onIntroChange,
  splash,
  onSplashChange,
  options,
  onOptionsChange,
  showExplainMap,
  onShowExplainMapChange,
  agent,
  personalKeys,
  onAgentChange,
  components,
  layers,
  disabled = false,
}: {
  value: string
  onChange: (value: string) => void
  intro: string
  onIntroChange: (value: string) => void
  splash: string
  onSplashChange: (value: string) => void
  options: string
  onOptionsChange: (value: string) => void
  showExplainMap: boolean
  onShowExplainMapChange: (value: boolean) => void
  agent: PublicAgentDraft
  personalKeys: PersonalAgentKey[]
  onAgentChange: (next: PublicAgentDraft) => void
  components: PublicShareItem[]
  layers: IntroLayerRef[]
  disabled?: boolean
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const introRef = React.useRef<HTMLTextAreaElement>(null)
  const [cursor, setCursor] = React.useState(0)
  const [highlight, setHighlight] = React.useState(0)
  const [dismissedQuery, setDismissedQuery] = React.useState<string | null>(null)
  const [introCursor, setIntroCursor] = React.useState(0)
  const [introHighlight, setIntroHighlight] = React.useState(0)
  const [introDismissedQuery, setIntroDismissedQuery] = React.useState<string | null>(null)

  const mention = mentionAtCursor(value, cursor, components.map(component => component.label))
  const introMention = mentionAtCursor(intro, introCursor, layers.map(layer => layer.label))
  const matches = React.useMemo(() => {
    if (!mention) return []
    const query = mention.query.trim().toLowerCase()
    return components.filter(component => {
      if (!query) return true
      const haystack = `${component.label} ${component.category ?? ""}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [components, mention])
  const introMatches = React.useMemo(() => {
    if (!introMention) return []
    const query = introMention.query.trim().toLowerCase()
    return layers.filter(layer => {
      if (!query) return true
      return layer.label.toLowerCase().includes(query)
    })
  }, [layers, introMention])
  const menuOpen = mention != null && dismissedQuery !== `${mention.start}:${mention.query}`
  const introMenuOpen = introMention != null && introDismissedQuery !== `${introMention.start}:${introMention.query}`

  React.useEffect(() => {
    setHighlight(0)
  }, [mention?.start, mention?.query])

  React.useEffect(() => {
    setIntroHighlight(0)
  }, [introMention?.start, introMention?.query])

  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const introOptionRefs = React.useRef<Array<HTMLButtonElement | null>>([])

  React.useEffect(() => {
    if (!menuOpen) return
    optionRefs.current[highlight]?.scrollIntoView({ block: "nearest" })
  }, [highlight, menuOpen])

  React.useEffect(() => {
    if (!introMenuOpen) return
    introOptionRefs.current[introHighlight]?.scrollIntoView({ block: "nearest" })
  }, [introHighlight, introMenuOpen])

  const syncCursor = (element: HTMLTextAreaElement) => {
    setCursor(element.selectionStart ?? 0)
  }

  const syncIntroCursor = (element: HTMLTextAreaElement) => {
    setIntroCursor(element.selectionStart ?? 0)
  }

  const insertComponent = (component: PublicShareItem) => {
    if (!mention) return
    const token = `@${component.label}: `
    const next = value.slice(0, mention.start) + token + value.slice(cursor)
    const nextCursor = mention.start + token.length
    onChange(next)
    setCursor(nextCursor)
    setDismissedQuery(null)
    window.requestAnimationFrame(() => {
      const element = textareaRef.current
      if (!element) return
      element.focus()
      element.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const insertLayer = (layer: IntroLayerRef) => {
    if (!introMention) return
    const token = layerMentionToken(layer)
    const next = intro.slice(0, introMention.start) + token + intro.slice(introCursor)
    const nextCursor = introMention.start + token.length
    onIntroChange(next)
    setIntroCursor(nextCursor)
    setIntroDismissedQuery(null)
    window.requestAnimationFrame(() => {
      const element = introRef.current
      if (!element) return
      element.focus()
      element.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const onIntroKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!introMenuOpen || introMatches.length === 0) {
      if (event.key === "Escape" && introMention) {
        event.preventDefault()
        setIntroDismissedQuery(`${introMention.start}:${introMention.query}`)
      }
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setIntroHighlight(index => (index + 1) % introMatches.length)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setIntroHighlight(index => (index - 1 + introMatches.length) % introMatches.length)
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      insertLayer(introMatches[Math.min(introHighlight, introMatches.length - 1)])
    } else if (event.key === "Escape") {
      event.preventDefault()
      setIntroDismissedQuery(`${introMention.start}:${introMention.query}`)
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menuOpen || matches.length === 0) {
      if (event.key === "Escape" && mention) {
        event.preventDefault()
        setDismissedQuery(`${mention.start}:${mention.query}`)
      }
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlight(index => (index + 1) % matches.length)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlight(index => (index - 1 + matches.length) % matches.length)
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      insertComponent(matches[Math.min(highlight, matches.length - 1)])
    } else if (event.key === "Escape") {
      event.preventDefault()
      setDismissedQuery(`${mention.start}:${mention.query}`)
    }
  }

  return (
    <>
      <div className="d-flex flex-column" style={{ minWidth: 0, flex: "1 1 0", minHeight: 0, gap: "1rem", overflow: "auto" }}>
        <div className="d-flex flex-column" style={{ flex: "1 1 0", minHeight: 0 }}>
          <h6 className="mb-2">AEGIS Instructions</h6>
          <div className="position-relative flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <textarea
              ref={textareaRef}
              className="form-control flex-grow-1"
              style={{ minHeight: 0, resize: "none" }}
              value={value}
              disabled={disabled}
              placeholder="Guide the public AEGIS experience. Type @ to mention a component, then add extra information about it."
              aria-label="AEGIS Instructions"
              aria-autocomplete="list"
              aria-expanded={menuOpen}
              onChange={event => {
                onChange(event.target.value)
                syncCursor(event.target)
              }}
              onClick={event => syncCursor(event.currentTarget)}
              onKeyUp={event => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === "Tab" || event.key === "Escape") return
                syncCursor(event.currentTarget)
              }}
              onKeyDown={onKeyDown}
            />
            {menuOpen && (
              <div
                className="dropdown-menu show py-1"
                role="listbox"
                aria-label="Components"
                style={{ top: "0.4rem", left: "0.4rem", right: "0.4rem", maxHeight: "16rem", overflowY: "auto" }}
              >
                {matches.length === 0 ? (
                  <div className="dropdown-item disabled">No matching components</div>
                ) : matches.map((component, index) => (
                  <button
                    key={component.id}
                    type="button"
                    role="option"
                    aria-selected={index === highlight}
                    className={`dropdown-item d-flex justify-content-between align-items-baseline${index === highlight ? " active" : ""}`}
                    onMouseDown={event => event.preventDefault()}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => insertComponent(component)}
                    ref={element => { optionRefs.current[index] = element }}
                  >
                    <span>{component.label}{component.active ? "*" : ""}</span>
                    {component.category && (
                      <span className={`small ml-2 ${index === highlight ? "" : "text-muted"}`}>{component.category}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex flex-column" style={{ flex: "0 0 auto" }}>
          <h6 className="mb-2">AEGIS options</h6>
          <textarea
            className="form-control"
            rows={4}
            style={{ resize: "none" }}
            value={options}
            disabled={disabled}
            placeholder={"What does this layer show?\nHow was this result calculated?"}
            aria-label="AEGIS options"
            onChange={event => onOptionsChange(event.target.value)}
          />
          <div className="text-muted small mt-1">One option per line. Each appears as a button in the chat.</div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="public-project-explain-map"
              checked={showExplainMap}
              disabled={disabled}
              onChange={event => onShowExplainMapChange(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="public-project-explain-map">
              Show “Explain this map”
            </label>
          </div>
          <PublicAgentKeyFields
            agent={agent}
            personalKeys={personalKeys}
            disabled={disabled}
            onChange={onAgentChange}
          />
        </div>
      </div>
      <div className="d-flex flex-column" style={{ minWidth: 0, flex: "1 1 0", minHeight: 0, gap: "1rem", overflow: "auto" }}>
        <div className="d-flex flex-column" style={{ flex: "1 1 0", minHeight: 0 }}>
          <h6 className="mb-2">Splash</h6>
          <textarea
            className="form-control flex-grow-1"
            style={{ minHeight: 0, resize: "none" }}
            value={splash}
            disabled={disabled}
            placeholder="Shown first on the public page. Use markdown for headings (# Title) and pictures (![caption](https://…))."
            aria-label="Splash"
            onChange={event => onSplashChange(event.target.value)}
          />
        </div>
        <div className="d-flex flex-column" style={{ flex: "1 1 0", minHeight: 0 }}>
          <h6 className="mb-2">AEGIS intro</h6>
          <div className="position-relative flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <textarea
              ref={introRef}
              className="form-control flex-grow-1"
              style={{ minHeight: 0, resize: "none" }}
              value={intro}
              disabled={disabled}
              placeholder="Hi! Ask me about @Habitat suitability. Type @ to link a layer."
              aria-label="AEGIS intro"
              aria-autocomplete="list"
              aria-expanded={introMenuOpen}
              onChange={event => {
                onIntroChange(event.target.value)
                syncIntroCursor(event.target)
              }}
              onClick={event => syncIntroCursor(event.currentTarget)}
              onKeyUp={event => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === "Tab" || event.key === "Escape") return
                syncIntroCursor(event.currentTarget)
              }}
              onKeyDown={onIntroKeyDown}
            />
            {introMenuOpen && (
              <div
                className="dropdown-menu show py-1"
                role="listbox"
                aria-label="Layers"
                style={{ top: "0.4rem", left: "0.4rem", right: "0.4rem", maxHeight: "16rem", overflowY: "auto" }}
              >
                {introMatches.length === 0 ? (
                  <div className="dropdown-item disabled">No matching layers</div>
                ) : introMatches.map((layer, index) => (
                  <button
                    key={layer.id}
                    type="button"
                    role="option"
                    aria-selected={index === introHighlight}
                    className={`dropdown-item${index === introHighlight ? " active" : ""}`}
                    onMouseDown={event => event.preventDefault()}
                    onMouseEnter={() => setIntroHighlight(index)}
                    onClick={() => insertLayer(layer)}
                    ref={element => { introOptionRefs.current[index] = element }}
                  >
                    {layer.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="text-muted small mt-1">Type @ to mention a layer. Each mention becomes a link. Leave blank to use the default greeting.</div>
        </div>
      </div>
    </>
  )
}

export const Toolbar = ({ backButtonPath, teamId, projectName, hasUnsavedChanges, currentTab, isProcessing, isLoading, loadingQueue, datasetOutputCache, projectLayers, setProjectName, saveProject, setCurrentTab, autoProcessing, setAutoProcessing, manualProcessing, setShowAP, showAP, setShowExtent, zoomLevel, mapViewZoom, mapViewCenter, isUserGuest, extraInfo, projectId, teamExtents, currentExtentId, projectModel, availableComponents, availableMapLayers }: ToolbarProps) => {
  const [shareUrl, setShareUrl] = React.useState<string | null>(null)
  const [shareError, setShareError] = React.useState<string | null>(null)
  const [copyFeedback, setCopyFeedback] = React.useState(false)
  const [showUploadModal, setShowUploadModal] = React.useState(false)
  const [showPublicShareModal, setShowPublicShareModal] = React.useState(false)
  const [publicSelection, setPublicSelection] = React.useState<Set<string>>(new Set())
  const [extentSelection, setExtentSelection] = React.useState<Set<string>>(new Set())
  const extentSelectionRef = React.useRef(extentSelection)
  extentSelectionRef.current = extentSelection
  const currentExtentIdRef = React.useRef(currentExtentId)
  currentExtentIdRef.current = currentExtentId
  const defaultExtentSelection = (): Set<string> => {
    const id = currentExtentIdRef.current
    if (id == null || !teamExtents.some(extent => extent.id === id)) return new Set()
    return new Set([`extent-${id}`])
  }
  const extentSelectionOrDefault = (saved: Set<string>): Set<string> => (
    saved.size > 0 ? saved : defaultExtentSelection()
  )
  const extentEditRef = React.useRef(0)
  const [aegisInstructions, setAegisInstructions] = React.useState("")
  const [aegisIntro, setAegisIntro] = React.useState("")
  const [splash, setSplash] = React.useState("")
  const [aegisOptions, setAegisOptions] = React.useState("")
  const [showExplainMap, setShowExplainMap] = React.useState(true)
  const [agentDraft, setAgentDraft] = React.useState<PublicAgentDraft>(EMPTY_AGENT_DRAFT)
  const [personalKeys, setPersonalKeys] = React.useState<PersonalAgentKey[]>([])
  const [autoRun, setAutoRun] = React.useState(false)
  const autoRunEditRef = React.useRef(0)
  const [lockExtent, setLockExtent] = React.useState(false)
  const lockExtentEditRef = React.useRef(0)
  const selectionEditRef = React.useRef(0)
  const publicSelectionRef = React.useRef(publicSelection)
  publicSelectionRef.current = publicSelection
  const selectionSaveChain = React.useRef(Promise.resolve())
  const [publicProject, setPublicProject] = React.useState<PublicProjectShare | null>(null)
  const [publicShareSaving, setPublicShareSaving] = React.useState(false)
  const [publicShareReady, setPublicShareReady] = React.useState(false)
  const [publicShareError, setPublicShareError] = React.useState<string | null>(null)
  const [publicLinkCopied, setPublicLinkCopied] = React.useState(false)
  const [shareCodeDraft, setShareCodeDraft] = React.useState("")
  const shareCodeFocused = React.useRef(false)
  const knownShareItemIdsRef = React.useRef<Set<string>>(new Set())
  const preserveSavedSelectionRef = React.useRef(false)
  const publicShareLoadRef = React.useRef(0)

  React.useEffect(() => {
    if (shareCodeFocused.current) return
    setShareCodeDraft(publicProject?.share_code ?? "")
  }, [publicProject?.share_code])

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "team-data-modal-close") {
        setShowUploadModal(false)
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  const availableMapLayerItems = React.useMemo(
    () => disambiguateLabels(availableMapLayers),
    [availableMapLayers]
  )
  const introLayers = React.useMemo(() => {
    const layers: IntroLayerRef[] = []
    Object.entries(projectLayers).forEach(([key, layer]) => {
      if (!layer || (layer.type === "DatasetLayer" && layer.deleted)) return
      const label = layer.name.trim()
      if (!label) return
      layers.push({
        id: Number(key),
        nodeId: layer.type === "ModelOutputLayer" ? layer.nodeId : undefined,
        label,
      })
    })
    layers.sort((a, b) => a.label.localeCompare(b.label) || a.id - b.id)
    return layers
  }, [projectLayers])
  const activeComponentNames = React.useMemo(() => {
    const names = new Set<string>()
    for (const node of Object.values(projectModel?.nodes ?? {})) {
      if (node.name) names.add(node.name)
    }
    return names
  }, [projectModel])
  const availableComponentItems = React.useMemo(
    () => disambiguateLabels(availableComponents
      .filter(component => !NON_INPUT_COMPONENT_CATEGORIES.has(component.category))
      .map(component => ({
        id: `available-${component.name}`,
        label: component.label,
        category: component.category || "Other",
        active: activeComponentNames.has(component.name),
      }))),
    [availableComponents, activeComponentNames]
  )
  const extentShareItems = React.useMemo<PublicShareItem[]>(
    () => teamExtents.map(extent => ({
      id: `extent-${extent.id}`,
      label: extentChoiceLabel(extent.name, extent.level),
    })),
    [teamExtents]
  )

  React.useEffect(() => {
    // Only a project that has never been shared defaults to every layer and
    // component. A saved share must keep its selection when the catalog
    // finishes loading after the dialog opens.
    if (!showPublicShareModal || !publicShareReady || preserveSavedSelectionRef.current) return
    setPublicSelection(prev => {
      let changed = false
      const next = new Set(prev)
      for (const item of [...availableMapLayerItems, ...availableComponentItems]) {
        if (knownShareItemIdsRef.current.has(item.id)) continue
        knownShareItemIdsRef.current.add(item.id)
        next.add(item.id)
        changed = true
      }
      if (changed) publicSelectionRef.current = next
      return changed ? next : prev
    })
  }, [showPublicShareModal, publicShareReady, availableMapLayerItems, availableComponentItems])

  React.useEffect(() => {
    if (!showPublicShareModal) return
    const loadId = ++publicShareLoadRef.current
    let cancelled = false
    fetch(`/projects/${projectId}/public_share`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then(async response => {
        const data = await response.json().catch(() => null)
        return { ok: response.ok, data }
      })
      .then(({ ok, data }) => {
        if (cancelled || loadId !== publicShareLoadRef.current) return
        if (!ok) {
          setPublicShareError("Could not load the saved share settings")
          return
        }
        const record = data?.public_project
        setPersonalKeys(personalKeysFromResponse(data))
        if (!record) {
          preserveSavedSelectionRef.current = false
          setAgentDraft(EMPTY_AGENT_DRAFT)
          setShowExplainMap(true)
          const initialExtents = defaultExtentSelection()
          extentSelectionRef.current = initialExtents
          setExtentSelection(initialExtents)
          setLockExtent(false)
          setPublicShareReady(true)
          return
        }
        preserveSavedSelectionRef.current = true
        setPublicProject(publicProjectFromRecord(record))
        setAegisInstructions(typeof record.aegis_instructions === "string" ? record.aegis_instructions : "")
        setAegisIntro(typeof record.aegis_intro === "string" ? record.aegis_intro : "")
        setSplash(typeof record.splash === "string" ? record.splash : "")
        setAegisOptions(typeof record.aegis_options === "string" ? record.aegis_options : "")
        setShowExplainMap(record.show_explain_map !== false)
        setAgentDraft(agentDraftFromRecord(record))
        if (autoRunEditRef.current === 0) setAutoRun(!!record.auto_run)
        if (lockExtentEditRef.current === 0) setLockExtent(!!record.lock_extent)
        const ids = [
          ...(Array.isArray(record.map_layer_ids) ? record.map_layer_ids : []),
          ...(Array.isArray(record.component_ids) ? record.component_ids : []),
        ]
        for (const id of ids) knownShareItemIdsRef.current.add(id)
        const saved = new Set(ids)
        publicSelectionRef.current = saved
        setPublicSelection(saved)
        selectionEditRef.current = 0
        const savedExtents = extentSelectionOrDefault(new Set<string>(
          (Array.isArray(record.extent_ids) ? record.extent_ids : []).map((id: unknown) => `extent-${id}`)
        ))
        extentSelectionRef.current = savedExtents
        setExtentSelection(savedExtents)
        extentEditRef.current = 0
        setPublicShareReady(true)
      })
      .catch(error => {
        if (cancelled || loadId !== publicShareLoadRef.current) return
        console.error("Error loading public share:", error)
        setPublicShareError("Could not load the saved share settings")
      })
    return () => { cancelled = true }
  }, [showPublicShareModal, projectId])

  const openPublicShareModal = () => {
    knownShareItemIdsRef.current = new Set()
    preserveSavedSelectionRef.current = true
    const initial = new Set<string>()
    publicSelectionRef.current = initial
    setPublicSelection(initial)
    selectionEditRef.current = 0
    const initialExtents = defaultExtentSelection()
    extentSelectionRef.current = initialExtents
    setExtentSelection(initialExtents)
    extentEditRef.current = 0
    autoRunEditRef.current = 0
    lockExtentEditRef.current = 0
    setLockExtent(false)
    setPublicShareReady(false)
    setPublicShareError(null)
    setShowPublicShareModal(true)
  }

  const enqueuePublicSelectionSave = (previous: Set<string>) => {
    selectionEditRef.current += 1
    const generation = selectionEditRef.current
    selectionSaveChain.current = selectionSaveChain.current
      .catch(() => undefined)
      .then(async () => {
        if (generation !== selectionEditRef.current) return
        const current = publicSelectionRef.current
        setPublicShareError(null)
        const response = await fetch(`/projects/${projectId}/public_share`, {
          method: "PATCH",
          headers: jsonHeaders(),
          credentials: "same-origin",
          body: JSON.stringify({
            map_layer_ids: Array.from(current).filter(id => id.startsWith("layer-")),
            component_ids: Array.from(current).filter(id => id.startsWith("available-")),
          }),
        })
        const data = await response.json().catch(() => ({}))
        if (generation !== selectionEditRef.current) return
        if (!response.ok || !data.public_project) {
          throw new Error(typeof data.error === "string" ? data.error : "Could not update the public project")
        }
        setPublicProject(publicProjectFromRecord(data.public_project))
      })
      .catch(error => {
        if (generation !== selectionEditRef.current) return
        publicSelectionRef.current = previous
        setPublicSelection(new Set(previous))
        setPublicShareError(error instanceof Error ? error.message : "Could not update the public project")
      })
  }

  const persistPublicSelection = (next: Set<string>) => {
    if (!publicShareReady) return
    const previous = publicSelectionRef.current
    publicSelectionRef.current = next
    setPublicSelection(next)
    if (!publicProject) {
      selectionEditRef.current += 1
      return
    }
    enqueuePublicSelectionSave(previous)
  }

  const persistExtentSelection = (next: Set<string>) => {
    if (!publicShareReady) return
    const previous = extentSelectionRef.current
    extentSelectionRef.current = next
    setExtentSelection(next)
    if (!publicProject) {
      extentEditRef.current += 1
      return
    }
    extentEditRef.current += 1
    const generation = extentEditRef.current
    selectionSaveChain.current = selectionSaveChain.current
      .catch(() => undefined)
      .then(async () => {
        if (generation !== extentEditRef.current) return
        setPublicShareError(null)
        const response = await fetch(`/projects/${projectId}/public_share`, {
          method: "PATCH",
          headers: jsonHeaders(),
          credentials: "same-origin",
          body: JSON.stringify({ extent_ids: extentIdsFromSelection(extentSelectionRef.current) }),
        })
        const data = await response.json().catch(() => ({}))
        if (generation !== extentEditRef.current) return
        if (!response.ok || !data.public_project) {
          throw new Error(typeof data.error === "string" ? data.error : "Could not update the public project")
        }
        setPublicProject(publicProjectFromRecord(data.public_project))
      })
      .catch(error => {
        if (generation !== extentEditRef.current) return
        extentSelectionRef.current = previous
        setExtentSelection(new Set(previous))
        setPublicShareError(error instanceof Error ? error.message : "Could not update the public project")
      })
  }

  const toggleExtentShareItem = (id: string) => {
    const next = new Set(extentSelectionRef.current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    persistExtentSelection(next)
  }

  const togglePublicShareItem = (id: string) => {
    const next = new Set(publicSelectionRef.current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    persistPublicSelection(next)
  }

  const setPublicShareGroup = (ids: string[], selected: boolean) => {
    const next = new Set(publicSelectionRef.current)
    for (const id of ids) {
      if (selected) next.add(id)
      else next.delete(id)
    }
    persistPublicSelection(next)
  }

  const jsonHeaders = () => {
    const headers = new Headers()
    headers.set("X-CSRF-Token", (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
    headers.set("Content-Type", "application/json")
    headers.set("Accept", "application/json")
    return headers
  }

  const savePublicProject = async () => {
    if (!publicShareReady) return
    if (agentDraft.keySource === "personal" && personalKeys.length === 0) {
      setPublicShareError("Save a personal API key on your account before using it here")
      return
    }
    if (agentDraft.keySource === "custom" && !agentDraft.keyPresent && !agentDraft.apiKey.trim()) {
      setPublicShareError("Enter an API key, or choose a personal key")
      return
    }
    if (agentDraft.keySource !== "" && !agentDraft.model) {
      setPublicShareError("Choose a model")
      return
    }
    publicShareLoadRef.current++
    setPublicShareError(null)
    setPublicShareSaving(true)
    try {
      const agentBody: { [key: string]: string } = {
        agent_key_source: agentDraft.keySource,
        agent_provider: agentDraft.keySource ? agentDraft.provider : "",
        agent_model: agentDraft.keySource ? agentDraft.model : "",
      }
      if (agentDraft.keySource === "custom" && agentDraft.apiKey.trim()) agentBody.agent_api_key = agentDraft.apiKey.trim()
      const response = await fetch(`/projects/${projectId}/public_share`, {
        method: "POST",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          map_layer_ids: Array.from(publicSelection).filter(id => id.startsWith("layer-")),
          component_ids: Array.from(publicSelection).filter(id => id.startsWith("available-")),
          aegis_instructions: aegisInstructions,
          aegis_intro: aegisIntro,
          splash,
          aegis_options: aegisOptions,
          show_explain_map: showExplainMap,
          auto_run: autoRun,
          lock_extent: lockExtent,
          extent_ids: extentIdsFromSelection(extentSelectionRef.current),
          ...(shareCodeDraft.trim() ? { share_code: shareCodeDraft.trim() } : {}),
          ...agentBody,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.public_project) {
        throw new Error(typeof data.error === "string" ? data.error : "Could not share this project")
      }
      setPublicProject(publicProjectFromRecord(data.public_project))
      setAgentDraft(agentDraftFromRecord(data.public_project))
      setPersonalKeys(personalKeysFromResponse(data))
    } catch (error) {
      setPublicShareError(error instanceof Error ? error.message : "Could not share this project")
    } finally {
      setPublicShareSaving(false)
    }
  }

  const persistAutoRun = async (next: boolean) => {
    autoRunEditRef.current += 1
    setAutoRun(next)
    if (!publicProject) return

    setPublicShareError(null)
    setPublicShareSaving(true)
    try {
      const response = await fetch(`/projects/${projectId}/public_share`, {
        method: "PATCH",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ auto_run: next }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.public_project) {
        throw new Error(typeof data.error === "string" ? data.error : "Could not update auto run")
      }
      setPublicProject(publicProjectFromRecord(data.public_project))
      setAutoRun(!!data.public_project.auto_run)
    } catch (error) {
      setAutoRun(!next)
      setPublicShareError(error instanceof Error ? error.message : "Could not update auto run")
    } finally {
      setPublicShareSaving(false)
    }
  }

  const persistLockExtent = async (next: boolean) => {
    lockExtentEditRef.current += 1
    setLockExtent(next)
    if (!publicProject) return

    setPublicShareError(null)
    setPublicShareSaving(true)
    try {
      const response = await fetch(`/projects/${projectId}/public_share`, {
        method: "PATCH",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ lock_extent: next }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.public_project) {
        throw new Error(typeof data.error === "string" ? data.error : "Could not update lock extent")
      }
      setPublicProject(publicProjectFromRecord(data.public_project))
      setLockExtent(!!data.public_project.lock_extent)
    } catch (error) {
      setLockExtent(!next)
      setPublicShareError(error instanceof Error ? error.message : "Could not update lock extent")
    } finally {
      setPublicShareSaving(false)
    }
  }

  const setPubliclyShared = async (publiclyShared: boolean) => {
    if (!publicProject) return
    setPublicShareError(null)
    setPublicShareSaving(true)
    const previous = publicProject.publicly_shared
    setPublicProject({ ...publicProject, publicly_shared: publiclyShared })
    try {
      const response = await fetch(`/projects/${projectId}/public_share`, {
        method: "PATCH",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ publicly_shared: publiclyShared }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.public_project) {
        throw new Error(typeof data.error === "string" ? data.error : "Could not update public sharing")
      }
      setPublicProject(publicProjectFromRecord(data.public_project))
    } catch (error) {
      setPublicProject({ ...publicProject, publicly_shared: previous })
      setPublicShareError(error instanceof Error ? error.message : "Could not update public sharing")
    } finally {
      setPublicShareSaving(false)
    }
  }

  const handleShare = async () => {
    setShareError(null)
    setShareUrl(null)
    try {
      const headers = new Headers()
      headers.set('X-CSRF-Token', (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content)
      headers.set('Content-Type', 'application/json')

      const response = await fetch(`/projects/${projectId}/share`, {
        method: 'POST',
        headers
      })

      if (!response.ok) {
        throw new Error('Failed to create shared project')
      }

      const data = await response.json()
      const baseUrl = data.share_url
      const params = new URLSearchParams()
      params.set('zoom', String(Math.round(mapViewZoom)))
      params.set('center', `${mapViewCenter[0]},${mapViewCenter[1]}`)
      const urlWithView = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`
      setShareUrl(urlWithView)
    } catch (error) {
      setShareError('Failed to create share link. Please try again.')
      console.error('Error sharing project:', error)
    }
  }

  const saveShareCode = async () => {
    if (!publicProject?.share_code) return
    const next = shareCodeDraft.trim()
    if (next === publicProject.share_code) return
    if (!next) {
      setShareCodeDraft(publicProject.share_code)
      setPublicShareError("Enter a link ending")
      return
    }
    if (next.length > 64 || !SHARE_CODE_PATTERN.test(next)) {
      setPublicShareError("Link endings can only use letters, numbers, hyphens, and underscores")
      return
    }
    setPublicShareError(null)
    setPublicShareSaving(true)
    try {
      const response = await fetch(`/projects/${projectId}/public_share`, {
        method: "PATCH",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ share_code: next }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.public_project) {
        throw new Error(typeof data.error === "string" ? data.error : "Could not update the share link")
      }
      setPublicProject(publicProjectFromRecord(data.public_project))
    } catch (error) {
      setPublicShareError(error instanceof Error ? error.message : "Could not update the share link")
    } finally {
      setPublicShareSaving(false)
    }
  }

  const handleCopyPublicLink = async () => {
    if (!publicProject?.share_url) return
    try {
      await navigator.clipboard.writeText(publicProject.share_url)
      setPublicLinkCopied(true)
      setTimeout(() => setPublicLinkCopied(false), 2000)
    } catch {
      setPublicShareError("Could not copy to clipboard")
    }
  }

  const handleCopyShareLink = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopyFeedback(true)
      setTimeout(() => setCopyFeedback(false), 2000)
    } catch {
      setShareError('Could not copy to clipboard')
    }
  }

  // --- Dataset loading progress (rendered as a small progress bar) -------
  // `loadingQueue` is the live list of dataset ids waiting to be loaded.
  // It's mutated in place when a layer is requested (see `loadTeamDataset`
  // in project_editor), so we can't rely on referential change alone.
  // Instead we accumulate every id we've ever seen during the current
  // batch into a ref-backed Set, which gives a stable "total this batch"
  // value even when items get added mid-load. The set is cleared once
  // loading has fully drained, ready for the next batch.
  const batchSeenRef = React.useRef<Set<number>>(new Set())
  if (!isLoading && loadingQueue.length === 0) {
    if (batchSeenRef.current.size !== 0) {
      batchSeenRef.current = new Set()
    }
  } else {
    for (const id of loadingQueue) {
      batchSeenRef.current.add(id)
    }
  }
  const batchTotal = batchSeenRef.current.size
  const batchCompleted = Math.max(0, batchTotal - loadingQueue.length)
  const batchPct = batchTotal > 0 ? Math.round((batchCompleted / batchTotal) * 100) : 0

  // Map dataset id -> display name so we can show which layer(s) are
  // currently being fetched instead of a generic spinner.
  const datasetNameById = React.useMemo(() => {
    const map = new Map<number, string>()
    for (const layer of Object.values(projectLayers)) {
      if (layer && layer.type === "DatasetLayer") {
        map.set(layer.id, layer.name || `Dataset #${layer.id}`)
      }
    }
    return map
  }, [projectLayers])

  const queuedNames = loadingQueue.map(id => datasetNameById.get(id) ?? `Dataset #${id}`)
  const currentName = queuedNames[0] ?? null
  const queueLabel = queuedNames.length === 0
    ? "Loading datasets…"
    : queuedNames.length === 1
      ? `Loading ${currentName}…`
      : `Loading ${currentName} (+${queuedNames.length - 1} more)…`

  return (
  <>
  <div className="btn-toolbar p-2 bg-light border-top">
    <div className="btn-group mr-2">
      <a className="btn btn-sm btn-outline-primary" href={backButtonPath}>
        <i className="fas fa-angle-left mr-1" />
        Back to projects
      </a>
    </div>
    <div className="input-group mr-2">
      <input type="text" className="form-control form-control-sm" value={projectName} disabled={isUserGuest} onChange={e => setProjectName(e.target.value)} />
    </div>
    {
      !isUserGuest &&
      <div className="btn-group mr-2">
        <button className="btn btn-sm btn-outline-primary" disabled={!hasUnsavedChanges} onClick={saveProject}>
          <i className="fas fa-save" /> Save
        </button>
      </div>    
    }
    <div className="btn-group mr-2 d-flex align-items-center flex-wrap">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary dropdown-toggle"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        disabled={isUserGuest}
      >
        <i className="fas fa-share" /> Share
      </button>
      <div className="dropdown-menu">
        <button type="button" className="dropdown-item" onClick={handleShare}>
          Embed link
        </button>
        <button type="button" className="dropdown-item" onClick={openPublicShareModal}>
          Share as public project
        </button>
      </div>
      {shareUrl != null && (
        <div className="input-group input-group-sm ml-2" style={{ maxWidth: 360 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            value={shareUrl}
            readOnly
            aria-label="Share link"
          />
          <div className="input-group-append">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCopyShareLink}
              title="Copy link"
            >
              {copyFeedback ? <><i className="fas fa-check" /> Copied!</> : <><i className="fas fa-copy" /> Copy</>}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setShareUrl(null); setShareError(null) }}
              title="Close"
              aria-label="Close share link"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>
      )}
      {shareError != null && (
        <span className="ml-2 small text-danger">{shareError}</span>
      )}
    </div>
    {
      <div className="btn-group mr-2">
      {
        currentTab == Tab.ModelView  &&
        <a onClick={()=> {
              const editUrl = `${window.location}/edit`
              if(hasUnsavedChanges) {
                if(confirm("You have unsaved changes. Are you sure you want to leave this page?") == true) window.location.href = editUrl
              }else{
                window.location.href = editUrl
              }
            }
          }
        >
          {
            !isUserGuest &&
            <button className={`btn btn-sm btn-outline-primary`}>
              <i className="fas fa-square" /> Extent
            </button>
          }
        </a>
      }
        <div onMouseEnter={() => setShowExtent(true)} onMouseLeave={() => setShowExtent(false)} title={`Zoom level = ${zoomLevel}`} className="p-1 " style={{backgroundColor: zoomLevel > 20 ? "green" : (zoomLevel < 20 ? "orange" : "yellow"), fontSize: ".9em"}}>
          {zoomLevel > 20 ? "High" : (zoomLevel < 20 ? "Low" : "Med")}
        </div>
      </div>
    }
    <div className="btn-group mr-2">
      <button className={`btn btn-sm ${currentTab == Tab.MapView ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setCurrentTab(Tab.MapView)}>
        <i className="fas fa-map-marked-alt" /> Map view
      </button>
      <button className={`btn btn-sm ${currentTab == Tab.ModelView ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setCurrentTab(Tab.ModelView)}>
        <i className="fas fa-project-diagram" /> Model view
      </button>
    </div>
    {
      currentTab == Tab.ModelView &&
      <div className="btn-group mr-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => window.dispatchEvent(new Event('model:zoomToFit'))}
          title="Zoom to fit"
        >
          <i className="fas fa-search-plus" /> Fit
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => window.dispatchEvent(new Event('model:undo'))}
          title="Undo model change"
        >
          <i className="fas fa-undo" /> Undo
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => window.dispatchEvent(new Event('model:redo'))}
          title="Redo model change"
        >
          <i className="fas fa-redo" /> Redo
        </button>
      </div>
    }
    {
      currentTab == Tab.MapView  &&
    <div className="btn-group mr-2">
      <button className={`btn btn-sm ${showAP ? "btn-primary" : "btn-outline-primary"}`} onClick={setShowAP}>
        <i className="fas fa-object-group" /> Snapshot
      </button>
    </div>
    }
    <div className="btn-group mr-2">
      <button title='Toggle between automatic and manual processing. Manual processing is recommended for larger models.' className={`btn btn-sm ${autoProcessing ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setAutoProcessing(!autoProcessing)}>
        {autoProcessing ? <><i className="fas fa-toggle-on" /> Auto </> : <><i className="fas fa-toggle-off" /> Auto </>}
      </button>
      {
        !autoProcessing &&
        <button disabled={isProcessing} className={`btn btn-sm btn-primary`} onClick={manualProcessing}>
          <i className="fas fa-play" /> Run
        </button>
      }
    </div>
    {
      isProcessing &&
      !extraInfo && 
      <div className="d-flex align-items-center">
        <div><i className="fas fa-circle-notch fa-spin" /> Processing...</div>
      </div>
    }
    {
      isProcessing &&
      extraInfo && 
      <div className="d-flex align-items-center">
        <div><i className="fas fa-circle-notch fa-spin" /> {extraInfo} </div>
      </div>
    }
    {
      (isLoading || loadingQueue.length > 0) && batchTotal > 0 &&
      <div
        className="d-flex align-items-center mr-2"
        style={{ minWidth: 220, maxWidth: 320 }}
        title={queuedNames.length > 0 ? `Pending:\n${queuedNames.join("\n")}` : undefined}
      >
        <div className="flex-grow-1">
          <div
            className="d-flex justify-content-between align-items-baseline"
            style={{ fontSize: "0.78rem", lineHeight: 1.15 }}
          >
            <span className="text-truncate" style={{ maxWidth: 220 }}>
              <i className="fas fa-cloud-download-alt mr-1 text-primary" />
              {queueLabel}
            </span>
            <span className="ml-2 text-muted" style={{ whiteSpace: "nowrap" }}>
              {batchCompleted}/{batchTotal}
            </span>
          </div>
          <div className="progress mt-1" style={{ height: "5px" }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
              role="progressbar"
              style={{ width: `${batchPct}%`, transition: "width 200ms linear" }}
              aria-valuenow={batchPct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      </div>
    }
    <div className="btn-group ml-auto">
      <button className="btn btn-sm btn-outline-primary" onClick={() => setShowUploadModal(true)} title="Upload data">
        <i className="fas fa-upload" />
      </button>
    </div>
  </div>
  {
    showUploadModal &&
    <div
      className="modal d-block"
      role="dialog"
      aria-modal="true"
      aria-labelledby="teamDataUploadModalTitle"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
      onClick={() => setShowUploadModal(false)}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content" style={{ minHeight: "75vh" }}>
          <div className="modal-header">
            <h5 className="modal-title" id="teamDataUploadModalTitle">Upload Data</h5>
            <button type="button" className="close" aria-label="Close" onClick={() => setShowUploadModal(false)}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body p-0" style={{ height: "75vh" }}>
            <iframe
              src={`/teams/${teamId}/team_data/new?modal=1`}
              title="Upload data"
              style={{ border: 0, width: "100%", height: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>
  }
  {
    showPublicShareModal &&
    <div
      className="modal d-block public-share-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="publicProjectShareModalTitle"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
      onClick={() => setShowPublicShareModal(false)}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content d-flex flex-column" style={{ height: "calc(100vh - 3.5rem)" }}>
          <div className="modal-header align-items-center" style={{ flexShrink: 0 }}>
            <h5 className="modal-title" id="publicProjectShareModalTitle">Share as public project</h5>
            <div className="d-flex align-items-center ml-auto">
              <div className="form-check mb-0 mr-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="public-project-auto-run"
                  checked={autoRun}
                  disabled={publicShareSaving || !publicShareReady}
                  onChange={event => persistAutoRun(event.target.checked)}
                />
                <label className="form-check-label" htmlFor="public-project-auto-run">
                  Auto run
                </label>
              </div>
              <button type="button" className="close" aria-label="Close" style={{ marginLeft: 0 }} onClick={() => setShowPublicShareModal(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body d-flex flex-column" style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
            <p className="text-muted small">
              Choose which map layers, components, and areas are available in the public project.
            </p>
            {!publicShareReady && !publicShareError && (
              <p className="text-muted small">Loading saved share settings…</p>
            )}
            <div className="d-flex flex-column flex-lg-row" style={{ gap: "1.5rem", flex: "1 1 auto", minHeight: 0 }}>
              <div className="d-flex flex-column" style={{ flex: "1 1 0", minWidth: 0, minHeight: 0, gap: "1rem" }}>
                <PublicShareChecklist
                  title="Map layers"
                  emptyLabel="Map layers are still loading."
                  items={availableMapLayerItems}
                  selectedIds={publicSelection}
                  onToggle={togglePublicShareItem}
                  onSetAll={setPublicShareGroup}
                  disabled={!publicShareReady}
                  sortItems={false}
                />
                <PublicShareChecklist
                  title="Components"
                  emptyLabel="Model components are still loading."
                  items={availableComponentItems}
                  selectedIds={publicSelection}
                  onToggle={togglePublicShareItem}
                  onSetAll={setPublicShareGroup}
                  disabled={!publicShareReady}
                />
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="public-project-lock-extent"
                    checked={lockExtent}
                    disabled={publicShareSaving || !publicShareReady}
                    onChange={event => persistLockExtent(event.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="public-project-lock-extent">
                    Lock extent for user
                  </label>
                </div>
                <PublicShareChecklist
                  title="Areas"
                  emptyLabel="This team has no extents yet."
                  items={extentShareItems}
                  selectedIds={extentSelection}
                  onToggle={toggleExtentShareItem}
                  onSetAll={(ids, selected) => {
                    const next = new Set(extentSelectionRef.current)
                    for (const id of ids) {
                      if (selected) next.add(id)
                      else next.delete(id)
                    }
                    persistExtentSelection(next)
                  }}
                  disabled={!publicShareReady}
                  sortItems={false}
                  showSelectAll={false}
                  filterable
                />
              </div>
              <AegisInstructionsEditor
                value={aegisInstructions}
                onChange={setAegisInstructions}
                intro={aegisIntro}
                onIntroChange={setAegisIntro}
                splash={splash}
                onSplashChange={setSplash}
                options={aegisOptions}
                onOptionsChange={setAegisOptions}
                showExplainMap={showExplainMap}
                onShowExplainMapChange={setShowExplainMap}
                agent={agentDraft}
                personalKeys={personalKeys}
                onAgentChange={setAgentDraft}
                components={availableComponentItems}
                layers={introLayers}
                disabled={!publicShareReady}
              />
            </div>
            <p className="text-muted small mb-0 mt-3">* Currently active layer or component</p>
          </div>
          <div className="modal-footer flex-wrap" style={{ flexShrink: 0 }}>
            {publicProject && (
              <div className="d-flex align-items-center flex-wrap mr-auto" style={{ gap: "0.75rem" }}>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="public-project-sharing"
                    checked={publicProject.publicly_shared}
                    disabled={publicShareSaving || !publicShareReady}
                    onChange={event => setPubliclyShared(event.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="public-project-sharing">
                    Sharing publicly
                  </label>
                </div>
                {publicProject.publicly_shared && publicProject.share_url && (
                  <div className="input-group input-group-sm" style={{ width: 460, maxWidth: "100%" }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text" title={shareLinkPrefix(publicProject.share_url, publicProject.share_code)} style={{ maxWidth: 240 }}>
                        <span className="text-truncate">{shareLinkPrefix(publicProject.share_url, publicProject.share_code)}</span>
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={shareCodeDraft}
                      spellCheck={false}
                      autoCapitalize="off"
                      autoCorrect="off"
                      aria-label="Share link ending"
                      disabled={publicShareSaving}
                      onFocus={() => { shareCodeFocused.current = true }}
                      onChange={event => setShareCodeDraft(event.target.value)}
                      onBlur={() => {
                        shareCodeFocused.current = false
                        void saveShareCode()
                      }}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault()
                          ;(event.target as HTMLInputElement).blur()
                        }
                      }}
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleCopyPublicLink}
                        title="Copy link"
                      >
                        {publicLinkCopied ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {publicShareError && <div className="small text-danger w-100">{publicShareError}</div>}
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPublicShareModal(false)} disabled={publicShareSaving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={savePublicProject} disabled={publicShareSaving || !publicShareReady}>
              {publicShareSaving ? "Sharing…" : "Share"}
            </button>
          </div>
        </div>
      </div>
    </div>
  }
  </>
  )
}
